// Mobile drawer — тёмная «панель-пульт» (тот же язык, что и desktop-сайдбар),
// выезжает слева. vaul-based Drawer из @studyhub/ui. Дизайн-язык «Картотека» §8.A.

import { Button } from "@studyhub/ui/components/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@studyhub/ui/components/collapsible";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerTitle,
} from "@studyhub/ui/components/drawer";
import { ScrollArea } from "@studyhub/ui/components/scroll-area";
import {
	IconArrowRight,
	IconChevronDown,
	IconChevronRight,
	IconFlame,
	IconSearch,
} from "@tabler/icons-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { forwardRef, type ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isDimmedPresentation, ModuleNavMarker } from "@/components/modules/module-nav-marker";
import {
	ALL_NAV_ITEMS,
	GUEST_SIDEBAR_GROUPS,
	isNavItemActive,
	type NavItem,
	SIDEBAR_GROUPS,
} from "@/components/navigation/nav-config";
import {
	type PresentedNavGroup,
	type PresentedNavItem,
	usePresentedNavGroups,
} from "@/components/navigation/use-nav-availability";
import { useNavBadges } from "@/components/navigation/use-nav-badges";
import { UserAvatar } from "@/components/shared/user-avatar";
import { useStreakState } from "@/components/streaks/_api";
import { StreakModal } from "@/components/streaks/streak-modal";
import { cn } from "@/lib/cn";
import { openAfterDrawerClose } from "@/lib/open-after-drawer-close";
import { resolveUserAvatarUrl } from "@/lib/user-avatar-url";
import { useCommandPaletteStore } from "@/stores/command-palette";
import { useLoginModalStore } from "@/stores/login-modal";
import { useSessionStore } from "@/stores/session";
import { BrandSparkle } from "./brand-sparkle";

interface MobileDrawerProps {
	isOpen: boolean;
	onOpenChange: (next: boolean) => void;
}

export const MobileDrawer = forwardRef<HTMLDivElement, MobileDrawerProps>(function MobileDrawer(
	{ isOpen, onOpenChange },
	_ref,
) {
	const { t } = useTranslation("layout");
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const navBadges = useNavBadges();
	const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
	const user = useSessionStore((s) => s.user);
	const openCmdK = useCommandPaletteStore((s) => s.open);
	const streakQuery = useStreakState();
	const currentStreak = streakQuery.data?.currentStreak ?? 0;
	const [streakOpen, setStreakOpen] = useState(false);
	// Свайп-влево закрывает меню (как на CharacterHub). Нативный vaul-drag для
	// left-drawer перехватывается внутренним ScrollArea, поэтому ловим жест сами:
	// горизонтально-доминантный свайп влево >56px → закрытие. Тап по ссылке (dx≈0)
	// и вертикальный скролл (dy>dx) не триггерят.
	const swipeStart = useRef<{ x: number; y: number } | null>(null);
	// Та же карта доступности, что и в desktop-сайдбаре: набор разделов на
	// мобильном обязан совпадать с десктопным, иначе паритет платформ ломается
	// молча (раздел «есть на компьютере, нет на телефоне»).
	const groups = usePresentedNavGroups(isAuthenticated ? SIDEBAR_GROUPS : GUEST_SIDEBAR_GROUPS);

	return (
		<>
			<Drawer
				open={isOpen}
				onOpenChange={onOpenChange}
				direction="left"
				shouldScaleBackground={false}
			>
				<DrawerContent
					onTouchStart={(e) => {
						const tch = e.touches[0];
						swipeStart.current = tch ? { x: tch.clientX, y: tch.clientY } : null;
					}}
					onTouchEnd={(e) => {
						const start = swipeStart.current;
						swipeStart.current = null;
						const tch = e.changedTouches[0];
						if (!start || !tch) return;
						const dx = tch.clientX - start.x;
						const dy = tch.clientY - start.y;
						if (dx < -56 && Math.abs(dx) > Math.abs(dy)) onOpenChange(false);
					}}
					className={cn(
						// inset-y-0 + h-full + mt-0 — на всю высоту экрана (база DrawerContent
						// рассчитана на bottom-sheet: mt-24 сдвигал панель вниз на 96px).
						// [&>div:first-child]:hidden — прячем grabber-«ручку» из базы (она
						// нужна bottom-sheet'ам, но не боковой навигации).
						"fixed inset-y-0 left-0 right-auto mt-0 h-full w-[284px] rounded-none",
						"bg-sidebar text-sidebar-foreground border-r border-sidebar-border",
						"flex flex-col p-0 px-3 pt-[env(safe-area-inset-top)]",
						"[&>div:first-child]:hidden",
					)}
				>
					<DrawerTitle className="sr-only">{t("mobileDrawer.title")}</DrawerTitle>
					<DrawerDescription className="sr-only">{t("mobileDrawer.description")}</DrawerDescription>

					{/* Логотип в шапке мобильного меню НЕ дублируем — он уже в топбаре. */}

					{/* Поиск (⌘K). На mobile-топбаре отдельной иконки поиска нет (ряд
				    разгружен), поэтому вход в командную палитру живёт здесь. */}
					{isAuthenticated && (
						<button
							type="button"
							onClick={() => {
								openAfterDrawerClose(() => onOpenChange(false), openCmdK);
							}}
							className={cn(
								"mt-3 flex min-h-11 w-full items-center gap-3 px-3 rounded-[9px] cursor-pointer",
								"text-[13.5px] text-sidebar-foreground transition-colors duration-fast",
								"hover:bg-white/[0.045] hover:text-sidebar-accent-foreground active:translate-y-px",
							)}
							aria-label={t("topbar.searchOpen")}
						>
							<IconSearch size={20} stroke={1.75} className="shrink-0" />
							<span className="truncate flex-1 text-left">{t("topbar.search")}</span>
						</button>
					)}

					{/* Nav groups */}
					<ScrollArea
						className="flex-1 min-h-0 -mx-1 px-1"
						aria-label={t("mobileDrawer.navAriaLabel")}
					>
						<nav aria-label={t("mobileDrawer.navAriaLabel")} className="pt-3 pb-1">
							{groups.map((group) => (
								<DrawerNavGroup
									key={group.id}
									group={group}
									pathname={pathname}
									navBadges={navBadges}
									onNavigate={() => onOpenChange(false)}
								/>
							))}
						</nav>
					</ScrollArea>

					{/* Footer: профиль (вынесен из шапки). Переключатель темы НЕ дублируем —
				    он уже в шапке (Topbar). */}
					<div className="border-t border-white/[0.06] pt-2 pb-[max(env(safe-area-inset-bottom),1rem)] flex flex-col gap-1 shrink-0">
						{/* Стрик — закрытие меню + открытие StreakModal (на mobile — bottom sheet). */}
						{isAuthenticated && currentStreak > 0 && (
							<button
								type="button"
								onClick={() => {
									openAfterDrawerClose(
										() => onOpenChange(false),
										() => setStreakOpen(true),
									);
								}}
								aria-label={t("streak.days", { count: currentStreak })}
								className={cn(
									"cat-streak flex items-center gap-2.5 min-h-12 px-2 rounded-[10px] text-left cursor-pointer",
									"bg-white/[0.045] border border-white/8 hover:bg-white/[0.07] active:translate-y-px",
								)}
							>
								<span className="inline-flex size-8 items-center justify-center rounded-[9px] bg-[var(--cb)] shrink-0">
									<IconFlame size={18} stroke={1.75} className="text-[var(--cf)]" />
								</span>
								<div className="min-w-0 flex-1">
									<div className="text-[13.5px] font-semibold tabular-nums text-(--sidebar-bright) leading-tight">
										{t("streak.days", { count: currentStreak })}
									</div>
									<p className="text-[11px] text-sidebar-foreground/70 leading-tight mt-0.5">
										{t("streak.encouragement")}
									</p>
								</div>
								<IconChevronRight size={16} className="shrink-0 text-sidebar-foreground/40" />
							</button>
						)}
						{isAuthenticated ? (
							<Link
								to="/settings"
								onClick={() => onOpenChange(false)}
								className={cn(
									"flex items-center gap-3 min-h-12 px-2 rounded-[10px] cursor-pointer",
									"text-sidebar-foreground transition-colors duration-fast",
									"hover:bg-white/[0.045] hover:text-sidebar-accent-foreground active:translate-y-px",
								)}
								aria-label={t("mobileDrawer.profile")}
							>
								<UserAvatar
									className="size-9 shrink-0"
									userId={user?.id}
									name={user?.name}
									avatarUrl={resolveUserAvatarUrl({ local: user?.avatarUrl })}
									avatarSeed={user?.avatarSeed}
									fallbackClassName="text-xs"
								/>
								<span className="min-w-0 flex-1">
									<span className="block truncate text-[13.5px] font-medium">
										{user?.name ?? t("mobileDrawer.profile")}
									</span>
									{user?.email ? (
										<span className="block truncate text-[11px] text-sidebar-foreground/55">
											{user.email}
										</span>
									) : null}
								</span>
								<IconChevronRight size={16} className="shrink-0 text-sidebar-foreground/40" />
							</Link>
						) : (
							<Button
								variant="default"
								size="sm"
								className="h-11 w-full gap-1.5"
								onClick={() => {
									openAfterDrawerClose(
										() => onOpenChange(false),
										() => useLoginModalStore.getState().open(),
									);
								}}
							>
								<span>{t("topbar.login")}</span>
								<IconArrowRight size={16} stroke={1.75} />
							</Button>
						)}
					</div>
				</DrawerContent>
			</Drawer>
			<StreakModal open={streakOpen} onClose={() => setStreakOpen(false)} />
		</>
	);
});

interface DrawerNavGroupProps {
	group: PresentedNavGroup;
	pathname: string;
	navBadges: Record<string, number>;
	onNavigate: () => void;
}

/**
 * Группа пунктов в мобильном drawer. Зеркалит desktop-сайдбар: группы с
 * `collapsible` («Инструменты») сразу раскрыты, маленькая eyebrow + chevron.
 * Авто-раскрытие, когда активный роут внутри. Триггер — тач-таргет ≥44px.
 */
function DrawerNavGroup({
	group,
	pathname,
	navBadges,
	onNavigate,
}: DrawerNavGroupProps): ReactElement {
	const { t } = useTranslation("layout");
	const [isOpen, setIsOpen] = useState(true);
	const expanded =
		isOpen || group.items.some((item) => isNavItemActive(pathname, item, ALL_NAV_ITEMS));

	const items = (
		<div className="space-y-0.5">
			{group.items.map((item) => (
				<DrawerNavItem
					key={item.to}
					item={item}
					pathname={pathname}
					badgeOverride={navBadges[item.to]}
					onNavigate={onNavigate}
				/>
			))}
		</div>
	);

	if (group.collapsible && group.eyebrowKey) {
		return (
			<Collapsible open={expanded} onOpenChange={setIsOpen} className="mb-1.5">
				<CollapsibleTrigger
					className="flex min-h-11 w-full cursor-pointer items-center gap-1 px-3 pt-2 pb-1 text-left"
					aria-label={t(group.eyebrowKey)}
				>
					<span className="min-w-0 flex-1 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-sidebar-foreground/55">
						{t(group.eyebrowKey)}
					</span>
					<IconChevronDown
						size={14}
						stroke={2}
						aria-hidden="true"
						className={cn(
							"shrink-0 text-sidebar-foreground/55 transition-transform duration-base",
							!expanded && "-rotate-90",
						)}
					/>
				</CollapsibleTrigger>
				<CollapsibleContent className="pt-0.5">{items}</CollapsibleContent>
			</Collapsible>
		);
	}

	return (
		<div className="mb-1.5">
			{group.eyebrowKey && (
				<div className="px-3 pt-3 pb-1.5 text-[10.5px] uppercase tracking-[0.16em] font-semibold text-sidebar-foreground/55">
					{t(group.eyebrowKey)}
				</div>
			)}
			{items}
		</div>
	);
}

function DrawerNavItem({
	item,
	pathname,
	badgeOverride,
	onNavigate,
}: {
	item: PresentedNavItem;
	pathname: string;
	badgeOverride?: number;
	onNavigate: () => void;
}) {
	const { t } = useTranslation("layout");
	const Icon = item.icon;
	const isActive = isNavItemActive(pathname, item, ALL_NAV_ITEMS);
	const badge = badgeOverride ?? item.badge;
	const label = t(item.labelKey);
	const isHashAnchor = item.to.includes("#");

	const containerClassName = cn(
		"group/nav relative flex items-center gap-3 min-h-11 px-3 rounded-[9px] cursor-pointer",
		"text-[13.5px] transition-[background-color,color,transform] duration-fast active:translate-y-px",
		isActive
			? "font-medium text-(--sidebar-bright) [background-image:linear-gradient(180deg,var(--sidebar-item-active-from),var(--sidebar-item-active-to))] shadow-[0_2px_0_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.07)]"
			: "text-sidebar-foreground hover:bg-white/[0.045] hover:text-sidebar-accent-foreground",
		isDimmedPresentation(item.presentation) && "opacity-60",
	);

	if (isHashAnchor) {
		return (
			<a href={item.to} onClick={onNavigate} className={containerClassName}>
				<DrawerNavInner
					Icon={Icon}
					label={label}
					isActive={isActive}
					badge={badge}
					badgeTone={item.badgeTone}
					sparkle={item.accent === "sparkle"}
					presentation={item.presentation}
				/>
			</a>
		);
	}

	const link = (
		<Link
			to={item.to}
			preload="intent"
			onClick={onNavigate}
			className={containerClassName}
			aria-current={isActive ? "page" : undefined}
		>
			<DrawerNavInner
				Icon={Icon}
				label={label}
				isActive={isActive}
				badge={badge}
				badgeTone={item.badgeTone}
				sparkle={item.accent === "sparkle"}
				presentation={item.presentation}
			/>
		</Link>
	);

	// Подпункты (Библиотека → Папки, Избранное) — та же логика, что в сайдбаре:
	// раскрываем только активную ветку, иначе дерево съедает высоту шторки.
	const children = item.children ?? [];
	const branchActive =
		isActive || children.some((child) => isNavItemActive(pathname, child, ALL_NAV_ITEMS));
	if (children.length === 0 || !branchActive) return link;

	return (
		<>
			{link}
			<div className="mt-0.5 space-y-0.5 pl-[22px]">
				{children.map((child) => (
					<DrawerNavItem key={child.to} item={child} pathname={pathname} onNavigate={onNavigate} />
				))}
			</div>
		</>
	);
}

function DrawerNavInner({
	Icon,
	label,
	isActive,
	badge,
	badgeTone,
	sparkle,
	presentation,
}: {
	Icon: NavItem["icon"];
	label: string;
	isActive: boolean;
	badge: number | undefined;
	badgeTone: NavItem["badgeTone"];
	sparkle: boolean;
	presentation: PresentedNavItem["presentation"];
}) {
	return (
		<>
			{isActive && (
				<span
					aria-hidden="true"
					className="absolute -left-3 top-2.5 bottom-2.5 w-[3px] rounded-r-[3px] bg-(--sidebar-accent-blue)"
				/>
			)}
			<Icon
				size={20}
				stroke={1.75}
				className={cn("shrink-0", isActive && "text-(--sidebar-icon-active)")}
			/>
			<span className="truncate flex-1">{label}</span>
			{sparkle && <BrandSparkle size={14} />}
			<ModuleNavMarker presentation={presentation} size={14} />
			{typeof badge === "number" && badge > 0 && (
				<span
					className={cn(
						"text-[11px] font-semibold tabular-nums rounded-md px-1.5 py-px",
						// «Видимый долг» (материалы вне курсов) — янтарная плитка.
						badgeTone === "warning"
							? "tint-warning"
							: isActive
								? "bg-white/10 text-(--sidebar-accent-foreground)"
								: "bg-white/[0.07] text-sidebar-foreground/80",
					)}
				>
					{badge}
				</span>
			)}
		</>
	);
}
